import {getStoreBuilder, BareActionContext} from "vuex-typex";
import RootState from "../index";
import {DateTime, JLocation} from "../../modules/models";
import {permissionsApi} from "../../modules/permissionsApi";
import {getCookie, setCookie} from "../../modules/cookies";
import {NativeApi, NativeApiExceptionCode} from "@/modules/NativeBridge";
import {AbortableDelay, AbortedException} from "@/modules/delay";

export type GeoAvailability = number;
export const geoNotChecked = 0 as GeoAvailability;
export const geoUnknown = 5 as GeoAvailability;
export const geoGranted = 10 as GeoAvailability;
export const geoGrantedInaccuratePromptable = 15 as GeoAvailability;
export const geoNotPrompted = 20 as GeoAvailability;
export const geoDeniedNonPromptable = 30 as GeoAvailability;
export const geoGrantedInaccurateNonpromptable = 31 as GeoAvailability;
export const geoNotAvailable = 40 as GeoAvailability;

export const numToGeoAvailability = (n: number): GeoAvailability => {
    switch (n) {
        case geoNotChecked: return geoNotChecked;
        case geoUnknown: return geoUnknown;
        case geoGranted: return geoGranted;
        case geoGrantedInaccuratePromptable: return geoGrantedInaccuratePromptable;
        case geoNotPrompted: return geoNotPrompted;
        case geoDeniedNonPromptable: return geoDeniedNonPromptable;
        case geoGrantedInaccurateNonpromptable: return geoGrantedInaccurateNonpromptable;
        case geoNotAvailable: return geoNotAvailable;
        default:
            throw new Error(`Uknown GeoAvailability number: ${n}`);
    }
};

type PositionState = number;
export const posInactive = 0;
export const posPending = 1;
export const posActive = 2;

export interface GeoState {
    availability: GeoAvailability;
    error: string | null;
    position: JLocation | null;
    watchid: number;
    positionState: PositionState;
}

export default interface GeoApi {
    readonly availability: GeoAvailability;
    readonly error: string | null;
    readonly position: JLocation | null;
    readonly active: boolean;
    readonly pending: boolean;
    checkAvailability(): Promise<void>;
    startGeolistener(): Promise<void>;
    stopGeolistener(): Promise<void>;
}

// browser and nativeApi actions must implement this
interface Actions {
    checkAvailability(context: BareActionContext<GeoState, RootState>): Promise<void>;
    startGeolistener(context: BareActionContext<GeoState, RootState>): void;
    stopGeolistener(context: BareActionContext<GeoState, RootState>): void;
}

export function initGeoModule(nativeApi: NativeApi) {
    const storeBuilder = getStoreBuilder<RootState>();

    const state = {
        availability: geoNotChecked,
        error: null,
        position: null,
        watchid: 0,
        positionState: posInactive,
    };
    const moduleBuilder = storeBuilder.module<GeoState>("geo", state);

    const geopermission = "geopermission"; // cookiename

    // mutations
    const mutations = {
        setAvailability: (state: GeoState, availability: GeoAvailability) => {
            state.availability = availability;
            setCookie(geopermission, availability.toString(), 900);
            if(availability == geoNotAvailable) {
                if(nativeApi.available) {
                    state.error = "Posisjonsinnhenting ikke mulig på din enhet.";
                } else {
                    state.error = "Posisjonsinnhenting ikke mulig i din nettleser.";
                }
            } else if(availability == geoDeniedNonPromptable) {
                if(nativeApi.available) {
                    state.error = `Posisjonsinnhenting er blokkert. Du kan manuelt gi tillatelse i app innstillingene.`;
                } else {
                    state.error = `Posisjonsinnhenting er blokkert. Fjern blokkeringen for ${window.location.host} i nettleseren hvis du vil registrere inn.`;
                }
            } else {
                state.error = null;
            }
            // Other availability states will be handled by gui, informing or prompting the user for action.
        },
        setError: (state: GeoState, error: string | null) => state.error = error,
        setPosition: (state: GeoState, position: JLocation | null) => state.position = position,
        setWatchid: (state: GeoState, watchid: number) => state.watchid = watchid,
        setPositionState: (state: GeoState, positionState: PositionState) => state.positionState = positionState,
    };
    // commits
    const commits = {
        setAvailability: moduleBuilder.commit(mutations.setAvailability),
        setError: moduleBuilder.commit(mutations.setError),
        setPosition: moduleBuilder.commit(mutations.setPosition),
        setWatchid: moduleBuilder.commit(mutations.setWatchid),
        setPositionState: moduleBuilder.commit(mutations.setPositionState),
    };
    // actions when using browser api
    const browserActions: () => Actions = () => ({
        async checkAvailability(context: BareActionContext<GeoState, RootState>) {
            if("geolocation" in navigator) {
                const permissions = permissionsApi(navigator);
                if (permissions != null) {
                    const status = await permissions.query({name: "geolocation"});
                    if(status.state == "granted") {
                        commits.setAvailability(geoGranted);
                    } else if(status.state == "denied") {
                        commits.setAvailability(geoDeniedNonPromptable);
                    } else if(status.state == "prompt") {
                        commits.setAvailability(geoNotPrompted);
                    } else {
                        console.warn("unexpected geolocation permissions status: "+status.state);
                        commits.setAvailability(geoUnknown);
                    }
                } else {
                    // permissions api is not available. Fallback to using cookie to determine if positioning has been allowed.
                    const status = Number(getCookie(geopermission));
                    if(status === geoGranted) {
                        commits.setAvailability(geoGranted);
                    } else if(status === geoDeniedNonPromptable) {
                        commits.setAvailability(geoDeniedNonPromptable);
                    } else if(status === 0 || status != status /* NaN */) { // cookie not set
                        commits.setAvailability(geoNotPrompted);
                    } else {
                        console.warn("unexpected geopermission cookie value: "+status);
                        commits.setAvailability(geoUnknown);
                    }
                }
            } else {
                commits.setAvailability(geoNotAvailable);
            }
        },
        startGeolistener(context: BareActionContext<GeoState, RootState>) {
            if(context.state.availability < geoUnknown || context.state.availability > geoNotPrompted) {
                throw new Error(`Cannot start geolocation listener. Invalid availability: ${context.state.availability}`);
            }
            commits.setPositionState(posPending);
            const opts = {
                timeout: 40000,  // Won't wait longer than 40 seconds for a position. NB: This seems to have no effect in Edge
                maximumAge: 60000 // Position can be max one minute old
            };
            const watchid = navigator.geolocation.watchPosition(
                (position) => {
                    commits.setPosition(JLocation.fromBrowserPosition(position));
                    if(context.state.availability != geoGranted) {
                        commits.setAvailability(geoGranted);
                    }
                    if(context.state.positionState != posActive) {
                        commits.setPositionState(posActive);
                    }
                },
                (err) => {
                    if(err.code === 1) {
                        commits.setAvailability(geoDeniedNonPromptable);
                    } else if(err.code === 2) {
                        commits.setAvailability(geoNotAvailable);
                    } else {
                        commits.setError(err.message);
                    }
                    commits.setPositionState(posInactive);
                },
                opts
            );
            commits.setWatchid(watchid);
        },
        stopGeolistener(context: BareActionContext<GeoState, RootState>) {
            navigator.geolocation.clearWatch(context.state.watchid);
            commits.setPositionState(posInactive);
        }
    });
    // actions when using nativeApi
    const nativeActions: () => Actions = () => {
        const geoNextDelay = new AbortableDelay();
        return {
            async checkAvailability(context: BareActionContext<GeoState, RootState>) {
                const status = await nativeApi.geoCheckAvailability();
                commits.setAvailability(status);
            },
            startGeolistener(context: BareActionContext<GeoState, RootState>): void {
                if(context.state.positionState > posInactive) { // Geolistener already started. Don't do anything more
                    console.info(`Geolistener already started. Will not do anything more`);
                    return;
                }
                const timeout = 15; // seconds
                const minDiffMeters = 3;
                commits.setPositionState(posPending);
                const errorHandlerFun = (err) => {
                    if(err instanceof AbortedException) {
                        console.info("pending geoNext aborted");
                    } else {
                        if (err.code == NativeApiExceptionCode.MISSING_PERMISSION_PROMPTABLE) {
                            commits.setAvailability(geoNotPrompted);
                        } else if (err.code == NativeApiExceptionCode.MISSING_PERMISSION_NON_PROMPTABLE) {
                            commits.setAvailability(geoDeniedNonPromptable);
                        } else if (err.code == NativeApiExceptionCode.GEOGRANTED_INACCURATE_PROMPTABLE) {
                            commits.setAvailability(geoGrantedInaccuratePromptable);
                        } else if (err.code == NativeApiExceptionCode.GEOGRANTED_INACCURATE_NON_PROMPTABLE) {
                            commits.setAvailability(geoGrantedInaccurateNonpromptable);
                        } else if (err.code == NativeApiExceptionCode.LOCATION_SERVICE_UNAVAIL) {
                            commits.setAvailability(geoNotAvailable);
                        } else {
                            commits.setError(err.message);
                        }
                        commits.setPositionState(posInactive);
                    }
                };
                let resultHandlerFun = (jloc: JLocation | null) => {
                    throw new Error("resultHandlerFun not setup");
                    // eslint-disable-next-line
                    return;
                };
                const minDelayMs = 3000; // Will not request geoNext more frequently than every 3 seconds
                let lastTimeMs = 0; // minDelayMs is comparea to this
                resultHandlerFun = async (jloc: JLocation | null) => {
                    commits.setPosition(jloc);
                    if(context.state.availability != geoGranted) {
                        commits.setAvailability(geoGranted);
                    }
                    if(jloc != null) {
                        if(context.state.positionState != posActive) {
                            commits.setPositionState(posActive);
                        }
                        // If less than minDelayMs since last position wait the diff time before calling geoNext again.
                        const timeToWaitMs = minDelayMs - (DateTime.now().millis() - lastTimeMs);
                        if(timeToWaitMs > 0) { // Wait a bit
                            // console.debug(`throttling geoNext call ${timeToWaitMs} milliseconds`);
                            await geoNextDelay.delay(timeToWaitMs, true);
                        }
                        lastTimeMs = DateTime.now().millis();
                        if(context.state.positionState > posInactive) { // As long as geolistener has not been stopped:
                            // Init listen for next position update
                            nativeApi.geoNext(jloc, timeout, minDiffMeters).then(resultHandlerFun).catch(errorHandlerFun);
                        }

                    } else {
                        if(context.state.positionState != posInactive) {
                            commits.setPositionState(posInactive);
                        }
                    }
                };
                nativeApi.geoNext(null, timeout, minDiffMeters).then(resultHandlerFun).catch(errorHandlerFun);
            },
            stopGeolistener(context: BareActionContext<GeoState, RootState>): void {
                geoNextDelay.abort(); // Incase there is pending geoNext, finish them now.
                nativeApi.geoStop();
                commits.setPositionState(posInactive);
            }
        }
    };
    // choose to use nativeActions or browserActions
    const actions: Actions = nativeApi.available && nativeApi.atleastVersion(0, 1, 0) ? nativeActions() : browserActions();

    const getters = {
        get state() {
            return moduleBuilder.state()();
        },
        get availability() {
            return this.state.availability;
        },
        get error() {
            return this.state.error;
        },
        get position() {
            return this.state.position;
        },
        get active() {
            return this.state.positionState == posActive;
        },
        get pending() {
            return this.state.positionState == posPending
        }
    };

    const dispatchers = {
        checkAvailability: moduleBuilder.dispatch(actions.checkAvailability),
        startGeolistener: moduleBuilder.dispatch(actions.startGeolistener),
        stopGeolistener: moduleBuilder.dispatch(actions.stopGeolistener),
    };

    // public api
    return {
        get availability() {
            return getters.availability;
        },
        get error() {
            return getters.error;
        },
        get position() {
            return getters.position;
        },
        get active() {
            return getters.active;
        },
        get pending() {
            return getters.pending;
        },
        ...dispatchers
    }
}
